import { Seo } from "../components/Seo";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, SlidersHorizontal, X, Download, FileText, Wrench, Sparkles, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { PageHero } from "../components/PageHero";
import { guides, GUIDE_TYPES, guideCountByType } from "../data/guides";
import { VG_ORANGE } from "../lib/brand";

const TYPE_ICON = { Installation: Wrench, Maintenance: Sparkles };

const FilterPanel = ({ selected, toggle, clearAll }) => (
  <div data-testid="guides-filters">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2">
        <SlidersHorizontal className="h-4 w-4" style={{ color: VG_ORANGE }} />
        <span className="font-heading font-bold text-neutral-900">Filters</span>
      </div>
      {selected.length > 0 && (
        <button onClick={clearAll} data-testid="clear-filters" className="text-xs font-medium text-neutral-500 hover:text-[#ED8B00]">
          Clear All
        </button>
      )}
    </div>

    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-3">Guide Type</h3>
      <div className="space-y-2.5">
        {GUIDE_TYPES.map((type) => (
          <label
            key={type}
            data-testid={`filter-${type.toLowerCase()}`}
            className="flex items-center gap-2.5 text-sm text-neutral-700 cursor-pointer hover:text-neutral-900"
          >
            <Checkbox checked={selected.includes(type)} onCheckedChange={() => toggle(type)} />
            <span className="flex-1">{type}</span>
            <span className="text-xs text-neutral-400">({guideCountByType(type)})</span>
          </label>
        ))}
      </div>
    </div>
  </div>
);

export default function ResourceCenter() {
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");

  const toggle = (type) =>
    setSelected((s) => (s.includes(type) ? s.filter((x) => x !== type) : [...s, type]));
  const clearAll = () => setSelected([]);

  const filtered = useMemo(() => {
    return guides.filter((g) => {
      const typeOk = selected.length === 0 || selected.includes(g.type);
      const qOk =
        g.title.toLowerCase().includes(q.toLowerCase()) ||
        (g.description || "").toLowerCase().includes(q.toLowerCase());
      return typeOk && qOk;
    });
  }, [selected, q]);

  return (
    <div data-testid="resources-page">
      <Seo title="Resource Center" description="Installation and maintenance guides for van Gelder commercial flooring — helping architects, facility managers, contractors, and project teams specify, install, and care for their floors." />
      <PageHero
        title="Resource Center"
        subtitle="Installation and maintenance guides to help you specify, install, and care for your van Gelder, Inc. flooring."
        testId="resources-hero"
      />

      <div className="mx-auto max-w-7xl px-4 py-10 grid gap-8 lg:grid-cols-[260px_1fr]">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-xl border border-neutral-200 bg-white p-5">
            <FilterPanel selected={selected} toggle={toggle} clearAll={clearAll} />
          </div>
        </aside>

        {/* Main */}
        <div>
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="lg:hidden">
                <Sheet>
                  <SheetTrigger asChild>
                    <button data-testid="mobile-filter-trigger" className="flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm font-medium">
                      <SlidersHorizontal className="h-4 w-4" /> Filters
                      {selected.length > 0 && (
                        <span className="ml-1 rounded-full bg-[#ED8B00] px-1.5 text-xs text-white">{selected.length}</span>
                      )}
                    </button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80 overflow-y-auto">
                    <div className="mt-6">
                      <FilterPanel selected={selected} toggle={toggle} clearAll={clearAll} />
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
              <p className="text-sm font-medium text-neutral-600" data-testid="guides-count">
                {filtered.length} Guide{filtered.length === 1 ? "" : "s"}
              </p>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <Input
                data-testid="guides-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search guides..."
                className="pl-9"
              />
            </div>
          </div>

          {/* Active filter chips */}
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              {selected.map((type) => (
                <button
                  key={type}
                  onClick={() => toggle(type)}
                  className="flex items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-xs font-medium text-[#ED8B00]"
                >
                  {type} <X className="h-3 w-3" />
                </button>
              ))}
            </div>
          )}

          {/* Grid */}
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((g) => {
              const Icon = TYPE_ICON[g.type] || FileText;
              const slug = g.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
              return (
                <a
                  key={g.pdf}
                  href={g.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`guide-card-${slug}`}
                  className="group flex flex-col rounded-xl border border-neutral-200 bg-white p-5 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="flex items-start justify-between">
                    <span
                      className="flex h-11 w-11 items-center justify-center rounded-lg"
                      style={{ backgroundColor: "#FFF3E6", color: VG_ORANGE }}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                      {g.type}
                    </span>
                  </div>
                  <h3 className="mt-4 font-heading text-base font-bold text-neutral-900 group-hover:text-[#ED8B00]">
                    {g.title}
                  </h3>
                  {g.description && (
                    <p className="mt-1.5 text-sm text-neutral-600 leading-relaxed">{g.description}</p>
                  )}
                  <span className="mt-auto pt-4 flex items-center gap-1.5 text-sm font-medium" style={{ color: VG_ORANGE }}>
                    <Download className="h-4 w-4" /> Download PDF
                  </span>
                </a>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="py-20 text-center text-neutral-500" data-testid="no-results">
              No guides match your filters.
              <button onClick={() => { clearAll(); setQ(""); }} className="ml-1 font-semibold text-[#ED8B00] hover:underline">
                Reset
              </button>
            </div>
          )}

          {/* About the Resource Library */}
          <section className="mt-14 border-t border-neutral-200 pt-12" data-testid="about-resource-library">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: VG_ORANGE }}>
              About the Resource Library
            </span>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-extrabold text-neutral-900">
              Expert Resources for Commercial Flooring Professionals
            </h2>
            <div className="mt-4 space-y-4 text-neutral-600 leading-relaxed">
              <p>
                Our Resource Library is designed to help architects, designers, facility managers,
                contractors, and project teams make informed flooring decisions with confidence.
                Here you'll find practical guides, educational articles, installation resources,
                maintenance recommendations, and technical insights developed by the commercial
                flooring professionals at van Gelder, Inc.
              </p>
              <p>
                Rather than marketing materials, these resources are created to support real-world
                projects—from early design and product specification through installation,
                maintenance, and long-term performance.
              </p>
            </div>

            <h3 className="mt-8 font-heading text-xl font-bold text-neutral-900">
              Practical Knowledge from Industry Experts
            </h3>
            <p className="mt-3 text-neutral-600 leading-relaxed">
              Every guide, article, and video is developed by the team that works with commercial
              flooring projects every day. Explore detailed topics including:
            </p>
            <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
              {[
                "Commercial flooring specification best practices",
                "Product comparisons and system recommendations",
                "Installation techniques and project preparation",
                "Cleaning and maintenance programs",
                "Material performance and lifecycle considerations",
                "Sustainability and commercial flooring innovations",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-neutral-700">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: VG_ORANGE }} />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-neutral-600 leading-relaxed">
              Whether you're designing a new facility, renovating an existing space, or managing
              multiple locations, our goal is to provide clear, practical information that helps you
              make better flooring decisions.
            </p>

            <h3 className="mt-8 font-heading text-xl font-bold text-neutral-900">
              Filter by content type, topic, industry, or product category
            </h3>
            <p className="mt-3 text-neutral-600 leading-relaxed">
              Use the filters on the left to narrow to what you need. Content types cover written
              articles, video walkthroughs, and downloadable PDF spec sheets. Topic filters cover
              installation, maintenance, specification, and sustainability. Industry filters isolate
              content specific to retail, healthcare, education, hospitality, and industrial spaces.
              Product category filters cross-reference the article to a specific SKU family, useful
              when you're already deep in a product spec and want the reference material that
              matches.
            </p>

            <h3 className="mt-8 font-heading text-xl font-bold text-neutral-900">
              Built for Commercial Flooring Decision Makers
            </h3>
            <div className="mt-3 space-y-4 text-neutral-600 leading-relaxed">
              <p>
                Our Resource Library is written for the professionals responsible for designing,
                specifying, purchasing, installing, and maintaining commercial flooring—not simply
                those browsing for inspiration.
              </p>
              <p>
                Architects and designers will find specification guidance, technical documentation,
                and material insights. Facility managers can access maintenance recommendations,
                lifecycle planning resources, and performance best practices. Contractors and
                installers benefit from installation guidance and product-specific technical
                information, while procurement teams can reference product data and planning
                resources for projects of every scale.
              </p>
            </div>

            <div className="mt-8 rounded-xl border border-neutral-200 bg-neutral-50 p-6">
              <p className="text-sm text-neutral-600 leading-relaxed">
                If there's a topic you'd like to see covered,{" "}
                <Link to="/contact" className="font-semibold hover:underline" style={{ color: VG_ORANGE }}>
                  let us know
                </Link>
                . We continually expand our Resource Library based on the questions, challenges, and
                projects our customers encounter every day.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
